import setup from '../assets/data/setup.json';
import { categorizeSkillsForResume, categorizeTechnicalExpertise } from './categorizeSkills';
import { generateProfessionalSummary, generateResumeHighlights } from './generators';
import type { BuiltResume, ResumeCertification, ResumeLayoutMode, ResumeVariantId } from './types';
import { getVariant, scoreItem } from './variants';

type ExperienceJson = (typeof setup.experiences)[number];
type ProjectJson = (typeof setup.projects)[number];

const EXECUTIVE_LIMITS = {
  maxExperiences: 2,
  maxProjects: 0,
  maxBullets: 2,
  maxHighlights: 5,
  maxCertifications: 2,
};

function buildExperiences(
  variant: ReturnType<typeof getVariant>,
  layoutMode: ResumeLayoutMode
): BuiltResume['experiences'] {
  const isExecutive = layoutMode === 'executive';
  const maxExperiences = isExecutive
    ? EXECUTIVE_LIMITS.maxExperiences
    : variant.maxExperiences;
  const maxBullets = isExecutive
    ? EXECUTIVE_LIMITS.maxBullets
    : setup.resumeConfig?.resumeMaxBullets ?? 99;

  const scored = setup.experiences
    .map((exp) => ({
      exp,
      score: scoreItem(
        `${exp.subtitle} ${exp.highlight} ${exp.role || ''} ${(exp.technologies || []).join(' ')} ${exp.bulletPoints.join(' ')}`,
        exp.tags,
        variant.keywords
      ),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, maxExperiences)
    .map(({ exp }) => mapExperience(exp, maxBullets));

  return scored;
}

function mapExperience(
  exp: ExperienceJson,
  maxBullets: number
): BuiltResume['experiences'][number] {
  return {
    company: exp.subtitle,
    role: exp.role || setup.profile.title,
    focus: exp.highlight,
    dates: exp.date,
    employmentType: exp.employmentType || 'Remote',
    location: exp.country,
    description: exp.bulletPoints[0] || exp.highlight,
    achievements: exp.bulletPoints.slice(0, maxBullets),
    technologies: exp.technologies || [],
  };
}

function buildProjects(
  variant: ReturnType<typeof getVariant>,
  layoutMode: ResumeLayoutMode
): BuiltResume['projects'] {
  if (layoutMode === 'executive') return [];

  return setup.projects
    .map((project) => ({
      project,
      score: scoreItem(
        `${project.title} ${project.description} ${(project.technologies || []).join(' ')} ${project.businessImpact || ''}`,
        project.tags,
        variant.keywords
      ),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, variant.maxProjects)
    .map(({ project }) => mapProject(project));
}

function mapProject(project: ProjectJson): BuiltResume['projects'][number] {
  return {
    title: project.title,
    description: project.description,
    businessImpact: project.businessImpact,
    technologies: project.technologies,
    github: project.github,
    liveDemo: project.liveDemo,
  };
}

export function buildResume(
  variantId: ResumeVariantId = 'fullstack',
  layoutMode: ResumeLayoutMode = 'professional'
): BuiltResume {
  const variant = getVariant(variantId);
  const slug = setup.profile.name.replace(/\s+/g, '-');
  const isExecutive = layoutMode === 'executive';
  const highlights = generateResumeHighlights().slice(
    0,
    isExecutive ? EXECUTIVE_LIMITS.maxHighlights : 12
  );
  const certifications = ((setup.certifications || []) as ResumeCertification[])
    .filter((cert) => (isExecutive ? cert.status === 'completed' : true))
    .slice(0, isExecutive ? EXECUTIVE_LIMITS.maxCertifications : undefined);

  return {
    variant: variant.id,
    variantLabel: isExecutive ? 'Executive' : variant.label,
    contact: {
      name: setup.profile.name,
      title: isExecutive ? setup.profile.title : variant.title,
      email: setup.profile.email,
      linkedin: setup.profile.linkedin,
      github: setup.profile.github,
      website: setup.profile.siteUrl,
      location: setup.resumeConfig?.location || 'Remote',
    },
    summary: generateProfessionalSummary(variant),
    highlights,
    technicalExpertise: isExecutive
      ? {}
      : categorizeTechnicalExpertise(variant.keywords),
    skills: categorizeSkillsForResume(variant.keywords),
    experiences: buildExperiences(variant, layoutMode),
    projects: buildProjects(variant, layoutMode),
    education: setup.education,
    certifications,
    yearsExperience: setup.stats.yearsExperience,
    filename: isExecutive
      ? `${slug}-executive-resume.pdf`
      : `${slug}-${variant.id}-resume.pdf`,
  };
}

export { RESUME_VARIANTS, getVariant } from './variants';
